<script setup lang="ts">
import { computed, onUnmounted, ref, watch, type ComponentPublicInstance } from "vue";
import { Leaf, X } from "@lucide/vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";
import type { ResponseRatingState } from "../composables/responseRating";
import { RATING_SCORES, ratingLabel } from "../utils/responseRating";
import { AI_RESPONSE_RATING_COMMENT_MAX_LENGTH } from "@/api/services/AiUsage";

// "How was this answer?" under an assistant reply: five leaves for a 1–5 score (a leaf because the
// product is about carbon, not stars — and the API only stores the integer, so the icon is ours to
// choose). Choosing a leaf sends the score at once and LOCKS the row: a rating is final, not a toggle.
// A comment is a second, optional step that re-sends the same score with the note, since the API's
// feedback write replaces rating and comment together. Once rated, the row closes itself after a few
// seconds — unless the user is writing a comment, in which case it waits for Send.
// Purely presentational: the parent owns the state and the network call.
const props = defineProps<{
  state: ResponseRatingState;
}>();

const emit = defineEmits<{
  rate: [rating: number, comment?: string];
  dismiss: [];
}>();

// Long enough to read "Thanks" and decide whether to add a note; short enough that the row does not
// sit above the next question.
const AUTO_CLOSE_MS = 8000;
// After a comment is sent there is nothing left to do — just long enough to see it went through.
const COMMENT_SENT_CLOSE_MS = 1500;

const hoveredScore = ref<number | null>(null);
const comment = ref("");
const commentSent = ref(false);

const isSending = computed(() => props.state.status === "sending");
const isSent = computed(() => props.state.status === "sent");
const isLocked = computed(() => isSending.value || isSent.value);
const committedScore = computed(() => props.state.rating ?? 0);
// The leaves follow the pointer before a score is committed; after that they show the committed score.
const displayScore = computed(() => (isLocked.value ? committedScore.value : hoveredScore.value ?? 0));
const scoreLabel = computed(() => (displayScore.value > 0 ? ratingLabel(displayScore.value) : "Pick a leaf"));
const canSendComment = computed(() => isSent.value && !commentSent.value && comment.value.trim().length > 0);
const showCommentForm = computed(() => isSent.value && !commentSent.value);

// Roving tabindex, as PlatformReviewCard: one leaf in the tab order, arrow keys move focus and score.
const rovingScore = computed(() => (committedScore.value > 0 ? committedScore.value : RATING_SCORES[0]));
const leafRefs = ref<Record<number, HTMLButtonElement | null>>({});
// Native <button>s only ever hand back an Element here — the ComponentPublicInstance branch is
// template-ref typing boilerplate for elements that could be components.
const setLeafRef = (element: Element | ComponentPublicInstance | null, score: number) => {
  leafRefs.value[score] = element as HTMLButtonElement | null;
};

function rate(score: number) {
  if (isLocked.value) return;
  emit("rate", score);
}

function onLeafKeydown(event: KeyboardEvent, score: number) {
  if (isLocked.value) return;
  const min = RATING_SCORES[0];
  const max = RATING_SCORES[RATING_SCORES.length - 1];
  if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
    event.preventDefault();
    const next = event.key === "ArrowRight" ? Math.min(score + 1, max) : Math.max(score - 1, min);
    leafRefs.value[next]?.focus();
    rate(next);
  }
}

let closeTimer: ReturnType<typeof setTimeout> | null = null;
function clearCloseTimer() {
  if (closeTimer !== null) clearTimeout(closeTimer);
  closeTimer = null;
}
function scheduleClose(delayMs: number) {
  clearCloseTimer();
  closeTimer = setTimeout(() => emit("dismiss"), delayMs);
}

// The score landed: start the countdown. Typing a comment cancels it (the user is still here).
watch(isSent, (sent) => {
  if (sent && !commentSent.value) scheduleClose(AUTO_CLOSE_MS);
});
watch(comment, (text) => {
  if (text.length > 0) clearCloseTimer();
  else if (isSent.value && !commentSent.value) scheduleClose(AUTO_CLOSE_MS);
});
onUnmounted(clearCloseTimer);

function sendComment() {
  if (!canSendComment.value || committedScore.value === 0) return;
  commentSent.value = true;
  emit("rate", committedScore.value, comment.value);
  scheduleClose(COMMENT_SENT_CLOSE_MS);
}
</script>

<template>
  <div
    class="chat-assistant-bubble rounded-8 px-px-12 py-px-8 gap-px-6 flex max-w-[85%] flex-col border"
    role="group"
    aria-label="Rate this answer">
    <div class="gap-px-8 flex items-center justify-between">
      <span class="chat-text-muted t-footnote">{{ isSent ? "Thanks for your feedback" : "How was this answer?" }}</span>
      <button
        type="button"
        class="chat-button-subtle focus-ring interactive-active duration-fast ease-standard p-px-2 rounded-full transition-colors"
        :aria-label="isSent ? 'Close' : 'Dismiss rating'"
        @click="emit('dismiss')">
        <X
          class="icon-xs"
          :stroke-width="1.5"
          aria-hidden="true" />
      </button>
    </div>

    <div class="gap-px-8 flex flex-wrap items-center">
      <div
        class="gap-px-2 flex items-center"
        role="radiogroup"
        aria-label="Rating, 1 to 5 leaves"
        :aria-readonly="isSent">
        <button
          v-for="score in RATING_SCORES"
          :key="score"
          :ref="(element) => setLeafRef(element, score)"
          type="button"
          role="radio"
          class="focus-ring rounded-4 duration-fast ease-standard p-px-2 transition-transform"
          :class="{ 'hover:scale-110': !isLocked, 'interactive-disabled': isSending, 'cursor-default': isSent }"
          :tabindex="score === rovingScore ? 0 : -1"
          :aria-label="`${score} of 5 — ${ratingLabel(score)}`"
          :aria-checked="committedScore === score"
          :disabled="isLocked"
          @mouseenter="hoveredScore = score"
          @mouseleave="hoveredScore = null"
          @click="rate(score)"
          @keydown="onLeafKeydown($event, score)">
          <Leaf
            class="icon-sm duration-fast ease-standard transition-colors"
            :class="score <= displayScore ? 'text-app-green-600' : 'chat-text-muted'"
            :stroke-width="1.5"
            :fill="score <= displayScore ? 'var(--color-app-green-400)' : 'none'"
            aria-hidden="true" />
        </button>
      </div>
      <LoadingSpinner
        v-if="isSending"
        size="xs"
        fill="gray" />
      <span
        v-else
        class="t-footnote"
        :class="displayScore > 0 ? 'chat-text-main' : 'chat-text-muted'"
        aria-hidden="true">
        {{ scoreLabel }}
      </span>
      <!-- Announced only on a committed score: the visible label follows the pointer. -->
      <span
        class="sr-only"
        role="status">
        {{ committedScore > 0 ? ratingLabel(committedScore) : "" }}
      </span>
    </div>

    <form
      v-if="showCommentForm"
      class="chat-input-shell gap-px-6 rounded-8 px-px-8 py-px-4 flex items-center border"
      @submit.prevent="sendComment">
      <input
        v-model="comment"
        type="text"
        name="rating-comment"
        :maxlength="AI_RESPONSE_RATING_COMMENT_MAX_LENGTH"
        placeholder="Anything to add? (optional)"
        aria-label="Comment on this answer"
        class="chat-input-field t-xs min-w-0 flex-1 bg-transparent focus:outline-none" />
      <button
        type="submit"
        class="chat-button-subtle focus-ring interactive-active t-caption-bold rounded-4 px-px-8 py-px-2 duration-fast ease-standard transition-colors"
        :class="{ 'interactive-disabled': !canSendComment }"
        :disabled="!canSendComment">
        Send
      </button>
    </form>
  </div>
</template>
